/* eslint-disable react/prop-types */
import React, { useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { showHideSnackbar } from "../../../../store/Snackbar/actions";
import RemoveIcon from "@material-ui/icons/Remove";
import { FieldArray, Form } from "formik";
import { Grid, Typography } from "@mui/material";
import AddIcon from "@material-ui/icons/Add";
import CheckIcon from "@material-ui/icons/Check";
import SettingsBackupRestoreIcon from "@material-ui/icons/SettingsBackupRestore";
import { useIntl } from "react-intl";
import Input from "../../../../components/Input";
import { uploadType } from "./FlashCardForm";
import MaterialPreviewCarousel from "../../../../components/MaterialPreview/MaterialPreviewCarousel";

const setTabValue = (setFieldValue, flashcardIndex, values, type) => {
  const newArray = [];
  values.flashcards[
    `${flashcardIndex}`
  ].flashcard_options_attributes.forEach((answer) => {
    if (answer?.id) {
      let deletedAns = {
        ...answer,
        correct: false,
        _destroy: true
      };
      newArray.push(deletedAns);
    }
  });

  const withTwoEmptyValues = [
    ...newArray,
    ...[
      {
        content: "",
        correct: false,
        flashcard_attachments_attributes: [],
        currentTab: type
      },
      {
        content: "",
        correct: false,
        flashcard_attachments_attributes: [],
        currentTab: type
      }
    ]
  ];

  const question = values.flashcards[`${flashcardIndex}`];

  question.flashcard_options_attributes = withTwoEmptyValues;

  setFieldValue(`flashcards[${flashcardIndex}]`, question);
};

const removeAnswer = (replace, answerIndex, answer) => {
  return () => {
    replace(answerIndex, {
      ...answer,
      correct: false,
      _destroy: true
    });
  };
};

const AddAnswer = (push, values, flashcardIndex) => {
  return () => {
    push({
      content: "",
      currentTab:
        values.flashcards[flashcardIndex]
          .flashcard_options_attributes[0].currentTab,
      flashcard_attachments_attributes: [],
      correct: false
    });
  };
};

function AnswerRow({
  answer,
  answerIndex,
  arr,
  renderContent,
  push,
  replace,
  values,
  flashcardIndex,
  flashcard,
  isMemoryOnly
}) {
  if (answer._destroy) return null;

  const isCorrect = Boolean(answer.correct);
  const activeAnswers = flashcard.flashcard_options_attributes.filter(
    (x) => !x._destroy
  );

  return (
    <div className="mx-5 d-flex">
      <div className="flex-fill w-100">
        {renderContent(answer, answerIndex)}
      </div>
      <div className="mx-2">
        {!isMemoryOnly && arr?.filter((x) => !x._destroy)[0] && (
          <div className="d-flex">
            <AddIcon
              className="me-2 mt-2 cursor-pointer"
              onClick={AddAnswer(push, values, flashcardIndex)}
            />
            <div className="mx-2 mt-2 cursor-pointer">
              {values.flashcards[
                flashcardIndex
              ].flashcard_options_attributes.every(
                (el) => el.correct === false
              ) === true ? (
                <CheckIcon
                  color="success"
                  className="me-2"
                  onClick={() => {
                    replace(answerIndex, {
                      ...answer,
                      correct: true
                    });
                  }}
                />
              ) : isCorrect ? (
                <span
                  className="d-flex align-items-center"
                  style={{ color: "#2e7d32" }}
                >
                  <CheckIcon style={{ fontSize: 18 }} />
                  <span
                    style={{
                      fontSize: "0.8rem",
                      fontWeight: 600,
                      marginLeft: 4,
                      whiteSpace: "nowrap"
                    }}
                  >
                    Correct
                  </span>
                  <SettingsBackupRestoreIcon
                    className="ms-2"
                    onClick={() => {
                      replace(answerIndex, {
                        ...answer,
                        correct: false
                      });
                    }}
                  />
                </span>
              ) : null}
            </div>
          </div>
        )}
      </div>
      {!isMemoryOnly && activeAnswers.length > 2 && (
        <div className="mx-2 mt-2 cursor-pointer">
          <RemoveIcon
            className="me-2"
            onClick={removeAnswer(replace, answerIndex, answer)}
          />
        </div>
      )}
    </div>
  );
}

function FlashCardAnswerForm({
  flashcard,
  flashcardIndex,
  setFieldValue,
  mode,
  values,
  formRef
}) {
  const { messages } = useIntl();

  const {
    finalTest: { answerText, upload, answersOptions }
  } = messages;
  const uploadAnswerRef = useRef();
  const dispatch = useDispatch();

  async function handleUploadSingleImage(e, AIndex, type) {
    let file = e?.target?.files[0];
    if (!file) return;

    if (!file.type.includes("image")) {
      dispatch(
        showHideSnackbar({
          type: "error",
          isOpen: true,
          message: "Please select image"
        })
      );
      return;
    }

    let url = URL.createObjectURL(file);
    let objFile = { file, url };

    if (type == "answer") {
      formRef.current.setFieldValue(
        `flashcards[${flashcardIndex}].flashcard_options_attributes[${AIndex}].flashcard_attachments_attributes`,
        [{ path: objFile, position: AIndex }]
      );
      if (uploadAnswerRef.current)
        uploadAnswerRef.current.value = null;
    }
    // Clear input value
    e.target.value = null;
  }

  function getCurrentTab(answers) {
    const hasText = answers.some(
      (a) => a?.content && a?.content?.trim() !== ""
    );

    const hasImage = answers.some(
      (a) =>
        a?.flashcard_attachments_attributes?.length &&
        a?.flashcard_attachments_attributes?.[0] !== ""
    );

    if (hasText && hasImage) return "text_image";
    if (hasImage) return "image";
    if (hasText) return "text";

    return "text";
  }

  useEffect(() => {
    if (mode !== "Edit") return;

    const currentTab = getCurrentTab(
      flashcard.flashcard_options_attributes
    );

    const needsUpdate = flashcard.flashcard_options_attributes
      .filter((answer) => !answer._destroy)
      .some((answer) => answer.currentTab !== currentTab);

    if (!needsUpdate) return;

    flashcard.flashcard_options_attributes.forEach((answer, idx) => {
      if (answer._destroy) return;
      if (answer.currentTab !== currentTab) {
        setFieldValue(
          `flashcards[${flashcardIndex}].flashcard_options_attributes[${idx}].currentTab`,
          currentTab
        );
      }
    });
  }, [
    mode,
    flashcard.flashcard_options_attributes,
    flashcardIndex,
    setFieldValue
  ]);

  const renderTextContent = (answer, answerIndex) => (
    <Input
      name={`flashcards[${flashcardIndex}].flashcard_options_attributes[${answerIndex}].content`}
      label={answerText}
      isRequired={true}
    />
  );

  const renderImageContent = (answer, answerIndex) => (
    <div className="w-50 text-center">
      <MaterialPreviewCarousel
        answerIndex={answerIndex}
        flashcardIndex={flashcardIndex}
        fieldPath={`flashcards[${flashcardIndex}].flashcard_options_attributes[${answerIndex}].flashcard_attachments_attributes`}
        type={uploadType.answer}
        attachments={answer?.flashcard_attachments_attributes}
        setFieldValue={setFieldValue}
        handleUploadSingleImage={handleUploadSingleImage}
        uploadRef={uploadAnswerRef}
        uploadImageLabel={upload}
        mode={mode}
      />
    </div>
  );

  const renderTextImageContent = (answer, answerIndex) => (
    <>
      <Input
        name={`flashcards[${flashcardIndex}].flashcard_options_attributes[${answerIndex}].content`}
        label={answerText}
        isRequired={true}
      />
      <div className="flex-fill">
        <Grid item xs={6} className="pb-0">
          <div className="w-100 text-center">
            <MaterialPreviewCarousel
              answerIndex={answerIndex}
              flashcardIndex={flashcardIndex}
              fieldPath={`flashcards[${flashcardIndex}].flashcard_options_attributes[${answerIndex}].flashcard_attachments_attributes`}
              type={uploadType.answer}
              attachments={answer?.flashcard_attachments_attributes}
              setFieldValue={setFieldValue}
              handleUploadSingleImage={handleUploadSingleImage}
              uploadRef={uploadAnswerRef}
              uploadImageLabel={upload}
              mode={mode}
            />
          </div>
        </Grid>
      </div>
    </>
  );

  const currentTab =
    flashcard.flashcard_options_attributes[0]?.currentTab || "text";

  const isMemoryOnly = flashcard.mode_eligibility === "memory_only";

  const getTabClassName = (tab) =>
    currentTab === tab
      ? "tab-pane fade show active"
      : "tab-pane fade";

  const getNavClassName = (tab) =>
    currentTab === tab ? "nav-link active" : "nav-link";

  const renderAnswerRows = (renderContent, push, replace) =>
    flashcard.flashcard_options_attributes.map(
      (answer, answerIndex, arr) => (
        <AnswerRow
          key={answer.id || answerIndex}
          answer={answer}
          answerIndex={answerIndex}
          arr={arr}
          renderContent={renderContent}
          push={push}
          replace={replace}
          values={values}
          flashcardIndex={flashcardIndex}
          flashcard={flashcard}
          isMemoryOnly={isMemoryOnly}
        />
      )
    );

  return (
    <div>
      <FieldArray
        name={`flashcards[${flashcardIndex}].flashcard_options_attributes`}
      >
        {({ push, replace }) => {
          return (
            <Form>
              <Typography
                variant="h6"
                component="h6"
                className="text-secondary my-4"
              >
                {answersOptions}
              </Typography>
              <ul className="nav nav-tabs">
                <li className="nav-item" role="presentation">
                  <button
                    className={getNavClassName("text")}
                    data-bs-toggle="tab"
                    data-bs-target={`#text_${flashcardIndex}`}
                    type="button"
                    role="tab"
                    aria-controls={`text_${flashcardIndex}`}
                    aria-selected="false"
                    onClick={() =>
                      setTabValue(
                        setFieldValue,
                        flashcardIndex,
                        values,
                        "text"
                      )
                    }
                  >
                    Text
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={getNavClassName("image")}
                    data-bs-toggle="tab"
                    data-bs-target={`#image_${flashcardIndex}`}
                    type="button"
                    role="tab"
                    aria-controls={`image_${flashcardIndex}`}
                    aria-selected="false"
                    onClick={() =>
                      setTabValue(
                        setFieldValue,
                        flashcardIndex,
                        values,
                        "image"
                      )
                    }
                  >
                    Image
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={getNavClassName("text_image")}
                    data-bs-toggle="tab"
                    data-bs-target={`#image_text_${flashcardIndex}`}
                    type="button"
                    role="tab"
                    aria-controls={`image_text_${flashcardIndex}`}
                    aria-selected="false"
                    onClick={() =>
                      setTabValue(
                        setFieldValue,
                        flashcardIndex,
                        values,
                        "text_image"
                      )
                    }
                  >
                    Image with Text
                  </button>
                </li>
              </ul>
              <div className="tab-content py-4" id="myTabContent">
                <div
                  className={getTabClassName("text")}
                  id={`text_${flashcardIndex}`}
                  role="tabpanel"
                  aria-labelledby={`text_${flashcardIndex}`}
                >
                  {renderAnswerRows(renderTextContent, push, replace)}
                </div>

                <div
                  className={getTabClassName("image")}
                  id={`image_${flashcardIndex}`}
                  role="tabpanel"
                  aria-labelledby={`image_${flashcardIndex}`}
                >
                  {renderAnswerRows(
                    renderImageContent,
                    push,
                    replace
                  )}
                </div>

                <div
                  className={getTabClassName("text_image")}
                  id={`image_text_${flashcardIndex}`}
                  role="tabpanel"
                  aria-labelledby={`image_text_${flashcardIndex}`}
                >
                  {renderAnswerRows(
                    renderTextImageContent,
                    push,
                    replace
                  )}
                </div>
              </div>
            </Form>
          );
        }}
      </FieldArray>
    </div>
  );
}

export default FlashCardAnswerForm;
